import _ from 'lodash';

import {
  PanelBankConfigState,
  type PanelBankDiff,
  type PanelBankSettings,
} from '../../../components/PanelBank/types';
import {type LinePlotSettings} from '../../../components/WorkspaceDrawer/Settings/types';
import {getDatadogRum} from '../../../util/datadog/datadogRum';
import {getFoundPanelSpecs, getPanelBankDiff} from '../../../util/panelbank';
import {
  PANEL_BANK_CUSTOM_CHARTS_NAME,
  PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
  PANEL_BANK_SYSTEM_NAME,
} from '../../../util/panelbankTypes';
import * as Panels from '../../../util/panels';
import {isSparseOverridesEnabled} from '../../../util/sparseOverridesFeatureFlag';
import {setInShallowClone} from '../../../util/utility';
import {type StateType as ExpectedPanelsState} from '../../expectedPanels/reducer';
import {lookupPart} from '../normalizerSupport';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {type PanelBankSectionConfigNormalized} from '../panelBankSectionConfig/types';
import {type ActionType, type ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import * as ActionsInternal from './actionsInternal';
import {immutableAddOrUpdatePanelsToSections} from './addOrUpdatePanelsToSections';
import type * as Types from './types';

export function getPanelBankConfig(
  state: ViewReducerState,
  ref: Types.Ref
): Types.PanelBankConfigNormalized {
  return lookupPart(state.parts, ref);
}

/**
 * Applies immutable updates to each section in a panel bank
 * with the provided `getUpdatedSection` callback.
 *
 * `getUpdatedSection` will provide a reference to the current
 * section config. If updates are needed, return a new copy of
 * the section with the changes applied. Otherwise, return the
 * current section config as-is.
 */
export const updateSections = (
  state: ViewReducerState,
  ref: Types.Ref,
  getUpdatedSection: (
    curSectionConfig: Readonly<PanelBankSectionConfigNormalized>,
    idx: number
  ) => PanelBankSectionConfigNormalized
): [ViewReducerState, ActionType] => {
  // set up new state
  const newState = setInShallowClone(
    state,
    ['parts', 'panel-bank-section-config'],
    Object.assign({}, state.parts['panel-bank-section-config'])
  );

  // track current sections state to allow undo
  const curSectionConfigs: PanelBankSectionConfigNormalized[] = [];

  // iterate over each section
  state.parts[ref.type][ref.id].sectionRefs.forEach((sectionRef, idx) => {
    const curSectionConfig = state.parts[sectionRef.type][sectionRef.id];
    curSectionConfigs.push(curSectionConfig);
    newState.parts[sectionRef.type][sectionRef.id] = getUpdatedSection(
      curSectionConfig,
      idx
    );
  });

  const inverseAction = ActionsInternal.setAllSections(ref, curSectionConfigs);

  return [newState, inverseAction];
};

export const immutableReadyPanelBank = (
  state: ViewReducerState,
  ref: Types.Ref
): ViewReducerState => {
  const panelBankConfig = state.parts[ref.type][ref.id];

  if (panelBankConfig.state === PanelBankConfigState.Ready) {
    return state;
  }

  // Special stuff that happens the first time you initialize the PanelBank
  // Sort sections by name, with these special behaviors:
  //   - Pinned sections are first and don't get sorted alphabetically
  //   - Custom Visualizations is first if found
  //   - System is second to last
  //   - Hidden Panels is last

  // Find the Hidden Panels section
  const hiddenSectionRef = panelBankConfig.sectionRefs.slice(-1)[0];
  const prevPanelBankSections = panelBankConfig.sectionRefs.map(
    sectionRef => state.parts[sectionRef.type][sectionRef.id]
  );
  const legacyCustomVizSectionRefIndex = _.findIndex(prevPanelBankSections, {
    name: PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  });
  // Find the Custom Visualizations section
  const legacyCustomVizSectionRef =
    legacyCustomVizSectionRefIndex > -1
      ? panelBankConfig.sectionRefs[legacyCustomVizSectionRefIndex]
      : null;

  const pinnedSectionRefs = panelBankConfig.sectionRefs.filter(
    r => state.parts[r.type][r.id].pinned
  );

  // Sort sections alphabetically by name
  const sortedSectionRefs = panelBankConfig.sectionRefs
    .filter(
      r =>
        r !== hiddenSectionRef &&
        r !== legacyCustomVizSectionRef &&
        !state.parts[r.type][r.id].pinned
    )
    .sort((a, b) => {
      const nameA = state.parts[a.type][a.id].name.toLowerCase();
      const nameB = state.parts[b.type][b.id].name.toLowerCase();

      // Ensure system is last
      const systemLower = PANEL_BANK_SYSTEM_NAME.toLowerCase();
      if (nameB === systemLower && nameA !== systemLower) {
        return -1;
      } else if (nameB !== systemLower && nameA === systemLower) {
        return 1;
      }

      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });

  // Add the Custom Visualizations section at the beginning, and the Hidden Panels section at the end
  const updatedSectionRefs = _.compact(
    pinnedSectionRefs
      .concat(
        legacyCustomVizSectionRef != null ? [legacyCustomVizSectionRef] : []
      )
      .concat(sortedSectionRefs)
      .concat(hiddenSectionRef)
  );

  let newState: ViewReducerState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...panelBankConfig,
          sectionRefs: updatedSectionRefs,
          state: PanelBankConfigState.Ready,
        },
      },
    },
  };

  // Open the first section by default, if nothing is open.
  if (
    updatedSectionRefs.length > 0 &&
    updatedSectionRefs.every(
      sectionRef => !state.parts[sectionRef.type][sectionRef.id].isOpen
    )
  ) {
    const firstSectionRef = updatedSectionRefs[0];

    newState = {
      ...newState,
      parts: {
        ...newState.parts,
        [firstSectionRef.type]: {
          ...newState.parts[firstSectionRef.type],
          [firstSectionRef.id]: {
            ...newState.parts[firstSectionRef.type][firstSectionRef.id],
            isOpen: true,
          },
        },
      },
    };
  }

  // Open the Custom Charts section if it exists
  const customChartSectionRef = updatedSectionRefs.find(
    a => state.parts[a.type][a.id].name === PANEL_BANK_CUSTOM_CHARTS_NAME
  );

  if (customChartSectionRef != null) {
    newState = {
      ...newState,
      parts: {
        ...newState.parts,
        [customChartSectionRef.type]: {
          ...newState.parts[customChartSectionRef.type],
          [customChartSectionRef.id]: {
            ...newState.parts[customChartSectionRef.type][
              customChartSectionRef.id
            ],
            isOpen: true,
          },
        },
      },
    };
  }

  return newState;
};

export const immutableSortSectionsByPinning = (
  state: ViewReducerState,
  ref: Types.Ref
): ViewReducerState => {
  const panelBankConfig = state.parts[ref.type][ref.id];
  const unpinnedsections = panelBankConfig.sectionRefs.filter(
    r => !state.parts[r.type][r.id].pinned
  );
  const pinnedSections = panelBankConfig.sectionRefs.filter(
    r => state.parts[r.type][r.id].pinned
  );
  const sectionRefsSortedByPinning = pinnedSections.concat(unpinnedsections);
  const isCurrentSectionRefsDifferent = panelBankConfig.sectionRefs.some(
    (sectionRef, i) => sectionRef !== sectionRefsSortedByPinning[i]
  );
  if (isCurrentSectionRefsDifferent) {
    return {
      ...state,
      parts: {
        ...state.parts,
        [ref.type]: {
          ...state.parts[ref.type],
          [ref.id]: {
            ...panelBankConfig,
            sectionRefs: sectionRefsSortedByPinning,
          },
        },
      },
    };
  }
  return state;
};

/**
 * Applies panel placement overrides after auto-generation.
 * This moves panels to their overridden sections and reorders them by orderKey.
 */
export const applyPanelPlacementOverrides = (
  state: ViewReducerState,
  ref: Types.Ref
): ViewReducerState => {
  if (!isSparseOverridesEnabled()) {
    return state;
  }

  const panelBankConfig = getPanelBankConfig(state, ref);
  const overrides = panelBankConfig.panelPlacementOverrides;

  if (!overrides || Object.keys(overrides).length === 0) {
    return state;
  }

  const startTime = performance.now();

  // Build lookup maps
  // Use getOverrideKey to include both metric-based keys and custom:__id__ keys
  const panelKeyToLocation = new Map<
    string,
    {
      panelRef: PanelTypes.Ref;
      sectionRef: PanelBankSectionConfigTypes.Ref;
    }
  >();
  const sectionIdToRef = new Map<string, PanelBankSectionConfigTypes.Ref>();

  for (const sectionRef of panelBankConfig.sectionRefs) {
    const section = state.parts[sectionRef.type][sectionRef.id];
    sectionIdToRef.set(section.__id__, sectionRef);

    for (const panelRef of section.panelRefs) {
      const panel = state.parts[panelRef.type][panelRef.id];
      // Use getOverrideKey to match both metric-based and custom:__id__ keys
      const overrideKey = Panels.getOverrideKey(panel);
      if (overrideKey != null) {
        panelKeyToLocation.set(overrideKey, {panelRef, sectionRef});
      }
    }
  }

  // Track which panels need to move to which sections
  const sectionMoves = new Map<
    string, // sectionRef.id
    {
      toAdd: PanelTypes.Ref[];
      toRemove: Set<string>; // panelRef.id
    }
  >();

  // Initialize move tracking for all sections
  for (const sectionRef of panelBankConfig.sectionRefs) {
    sectionMoves.set(sectionRef.id, {toAdd: [], toRemove: new Set()});
  }

  // Process overrides to determine moves
  let matchedCount = 0;
  for (const [panelKey, override] of Object.entries(overrides)) {
    const location = panelKeyToLocation.get(panelKey);
    if (!location) {
      // Orphaned override - panel doesn't exist
      continue;
    }

    const targetSectionRef = sectionIdToRef.get(override.sectionId);
    if (!targetSectionRef) {
      // Orphaned override - section doesn't exist
      continue;
    }

    matchedCount++;

    // Check if panel needs to move to a different section
    const currentSection =
      state.parts[location.sectionRef.type][location.sectionRef.id];

    if (currentSection.__id__ !== override.sectionId) {
      // Panel needs to move
      const fromMoves = sectionMoves.get(location.sectionRef.id)!;
      const toMoves = sectionMoves.get(targetSectionRef.id)!;

      fromMoves.toRemove.add(location.panelRef.id);
      toMoves.toAdd.push(location.panelRef);
    }
  }

  // Apply moves and sort sections
  let newState = state;

  for (const sectionRef of panelBankConfig.sectionRefs) {
    const section = newState.parts[sectionRef.type][sectionRef.id];
    const moves = sectionMoves.get(sectionRef.id)!;

    // Build new panelRefs array: remove outgoing, add incoming.
    // NOTE: We intentionally do NOT sort panelRefs here.
    // panelRefs stays in canonical (auto-generated) order. The display
    // ordering from overrides is applied at render time in the
    // usePanelBankConfig hook. This prevents the rearranged order
    // from being persisted/cached, which would corrupt positional keys.
    let newPanelRefs = section.panelRefs.filter(
      ref => !moves.toRemove.has(ref.id)
    );
    newPanelRefs = [...newPanelRefs, ...moves.toAdd];

    // Update state if panelRefs changed
    if (newPanelRefs !== section.panelRefs) {
      newState = setInShallowClone(
        newState,
        ['parts', sectionRef.type, sectionRef.id, 'panelRefs'],
        newPanelRefs
      );
    }
  }

  const overrideCount = Object.keys(overrides).length;
  let totalPanelCount = 0;
  for (const sectionRef of panelBankConfig.sectionRefs) {
    totalPanelCount +=
      newState.parts[sectionRef.type][sectionRef.id].panelRefs.length;
  }

  const duration = performance.now() - startTime;

  getDatadogRum().addDurationVital('sparse_overrides.apply_placement', {
    startTime,
    duration,
  });
  getDatadogRum().addAction('sparse_overrides.apply_placement', {
    duration,
    overrideCount,
    matchedCount,
    totalPanelCount,
  });

  return newState;
};

/**
 * Applies config overrides (e.g., config, layout) to panels after
 * auto-generation. This restores customizations that were saved as sparse
 * overrides rather than modifying the panel definition directly.
 */
export const applyPanelConfigOverrides = (
  state: ViewReducerState,
  ref: Types.Ref
): ViewReducerState => {
  if (!isSparseOverridesEnabled()) {
    return state;
  }

  const panelBankConfig = getPanelBankConfig(state, ref);
  const overrides = panelBankConfig.panelConfigOverrides;

  if (!overrides || Object.keys(overrides).length === 0) {
    return state;
  }

  const overrideCount = Object.keys(overrides).length;

  const startTime = performance.now();

  let newState = state;
  let matchedCount = 0;

  for (const sectionRef of panelBankConfig.sectionRefs) {
    const section = newState.parts[sectionRef.type][sectionRef.id];
    for (const panelRef of section.panelRefs) {
      const panel = newState.parts[panelRef.type][panelRef.id];
      const overrideKey = Panels.getOverrideKey(panel);
      if (overrideKey == null) {
        continue;
      }

      const override = overrides[overrideKey];
      if (!override) {
        continue;
      }

      matchedCount++;

      let updatedPanel = panel;

      if (override.config != null) {
        updatedPanel = {
          ...updatedPanel,
          config: override.config,
        } as PanelTypes.Panel;
      }

      if (override.layout != null) {
        updatedPanel = {
          ...updatedPanel,
          layout: {
            ...updatedPanel.layout,
            ...override.layout,
          },
        };
      }

      if (updatedPanel !== panel) {
        newState = setInShallowClone(
          newState,
          ['parts', panelRef.type, panelRef.id],
          updatedPanel
        );
      }
    }
  }

  const duration = performance.now() - startTime;

  getDatadogRum().addDurationVital('sparse_overrides.apply_config', {
    startTime,
    duration,
  });
  getDatadogRum().addAction('sparse_overrides.apply_config', {
    duration,
    overrideCount,
    matchedCount,
  });

  return newState;
};

// NOTE: Section ordering from overrides is applied at render time in the
// usePanelBankConfig hook (see applySectionOrdering in hooks.ts), not here.

export const immutableDiffAndInitPanels = (
  state: ViewReducerState,
  ref: Types.Ref,
  expectedPanelSpecs: ExpectedPanelsState,
  shouldAlphaInsert?: boolean,
  shouldAutoGeneratePanels?: boolean
): ViewReducerState => {
  // can't diff if there's no expected specs
  if (!expectedPanelSpecs) {
    return state;
  }

  // compute panel bank diff
  const panelBankConfig = state.parts[ref.type][ref.id];
  const autoSectionNames = new Set<string>();
  const panelParts = panelBankConfig.sectionRefs.flatMap(sectionRef => {
    const section = state.parts[sectionRef.type][sectionRef.id];
    if (section.isPanelsAuto) {
      autoSectionNames.add(section.name);
    }
    return section.panelRefs.map(
      panelRef => state.parts[panelRef.type][panelRef.id]
    );
  });
  const sparseOverridesEnabled = isSparseOverridesEnabled();
  const shouldIgnorePanelForDiff = (panel: PanelTypes.Panel) =>
    sparseOverridesEnabled &&
    Panels.hasSparseCustomPanelPlacementOverride(
      panel,
      panelBankConfig.panelPlacementOverrides
    );
  const foundPanelSpecs = getFoundPanelSpecs(
    panelParts,
    expectedPanelSpecs.apiAddedSpecs,
    {shouldIgnorePanel: shouldIgnorePanelForDiff}
  );
  // Sparse-created custom panels carry custom placement overrides and should
  // not suppress regeneration of the corresponding auto panel. Legacy edited
  // panels may also have isAuto=false, but without the custom override they
  // still represent the metric already present in the workspace.
  const keysInPanels = _.compact(
    panelParts
      .filter(panel => !shouldIgnorePanelForDiff(panel))
      .map(panel => Panels.getKey(panel))
  );
  const panelBankDiff = getPanelBankDiff(
    expectedPanelSpecs,
    foundPanelSpecs,
    autoSectionNames,
    keysInPanels,
    panelBankConfig.state,
    shouldAutoGeneratePanels
  );

  if (!panelBankDiff.length) {
    // Even if no diff, still apply overrides (for cross-section moves and config)
    let result = applyPanelPlacementOverrides(state, ref);
    result = applyPanelConfigOverrides(result, ref);
    return result;
  }

  const newParts = immutableAddOrUpdatePanelsToSections(
    panelBankDiff,
    state.parts,
    ref,
    true,
    shouldAlphaInsert
  );

  let newState = {
    ...state,
    parts: newParts,
  };

  newState = immutableReadyPanelBank(newState, ref);
  newState = immutableSortSectionsByPinning(newState, ref);

  // Apply panel placement overrides after panel generation
  newState = applyPanelPlacementOverrides(newState, ref);

  // Apply panel config overrides after placement
  newState = applyPanelConfigOverrides(newState, ref);

  return newState;
};

const isPanelBankEmpty = (state: ViewReducerState, ref: Types.Ref) => {
  const {sectionRefs} = state.parts[ref.type][ref.id];
  if (!sectionRefs.length) {
    return true;
  }
  if (sectionRefs.length === 1) {
    // 'Hidden Panels' section doesn't count
    const sectionRef = sectionRefs[0];
    const section = state.parts[sectionRef.type][sectionRef.id];
    return section.name === PANEL_BANK_HIDDEN_SECTION_NAME;
  }
  return false;
};

function expandSections(
  state: ViewReducerState,
  ref: Types.Ref,
  sectionNames: Set<string>
): ViewReducerState {
  let newState = state;
  const panelBank = state.parts[ref.type][ref.id];
  panelBank.sectionRefs.forEach(sectionRef => {
    const section = state.parts[sectionRef.type][sectionRef.id];
    if (sectionNames.has(section.name) && !section.isOpen) {
      newState = setInShallowClone(
        newState,
        ['parts', sectionRef.type, sectionRef.id, 'isOpen'],
        true
      );
    }
  });
  return newState;
}

export const addPanelsBySpec = (
  prevState: ViewReducerState,
  ref: Types.Ref,
  specs: Types.KeySpecList,
  shouldAlphaInsert?: boolean
): [ViewReducerState, ActionType] => {
  // Track section names that will receive new panels
  const sectionNames = new Set<string>();
  const diff: PanelBankDiff = [];
  specs.forEach(({key, spec}) => {
    sectionNames.add(spec.defaultSection);
    diff.push({
      key,
      operation: {type: 'add', spec},
    });
  });
  const newParts = immutableAddOrUpdatePanelsToSections(
    diff,
    prevState.parts,
    ref,
    false,
    shouldAlphaInsert
  );
  let newState = {...prevState, parts: {...newParts}};

  // Apply special first-time processing if adding to an empty panel bank
  if (isPanelBankEmpty(prevState, ref)) {
    newState = setInShallowClone(
      newState,
      ['parts', ref.type, ref.id, 'state'],
      PanelBankConfigState.Init
    );
  }
  newState = immutableReadyPanelBank(newState, ref);

  // Respect pinned sections
  newState = immutableSortSectionsByPinning(newState, ref);

  // Auto-expand sections that received new panels
  newState = expandSections(newState, ref, sectionNames);

  // Set up undo action
  const undoMetadata = getAddPanelsBySpecUndoMetadata(prevState, newState, ref);
  const inverseAction = Actions.addPanelsBySpecUndo(ref, undoMetadata);

  return [newState, inverseAction];
};

export const addPanelsBySpecUndo = (
  prevState: ViewReducerState,
  ref: Types.Ref,
  sectionsMetadata: Types.UndoAddPanelsBySpecSectionMetadata[]
): [ViewReducerState, ActionType] => {
  let newState = prevState;

  // Map sectionsMetadata to just sectionRefs; this will become the new panelBankConfig.sectionRefs
  const newSectionRefs = sectionsMetadata.map(
    ({sectionRef, isPanelsAuto, panelRefs, isOpen}) => {
      // Restore section isPanelsAuto and panelRefs while we're at it
      newState = setInShallowClone(
        newState,
        ['parts', sectionRef.type, sectionRef.id, 'isPanelsAuto'],
        isPanelsAuto
      );
      if (panelRefs !== undefined) {
        newState = setInShallowClone(
          newState,
          ['parts', sectionRef.type, sectionRef.id, 'panelRefs'],
          panelRefs
        );
      }
      // Restore section isOpen state if it was changed
      if (isOpen !== undefined) {
        newState = setInShallowClone(
          newState,
          ['parts', sectionRef.type, sectionRef.id, 'isOpen'],
          isOpen
        );
      }
      return sectionRef;
    }
  );

  // Update panelBankConfig.sectionRefs
  newState = setInShallowClone(
    newState,
    ['parts', ref.type, ref.id, 'sectionRefs'],
    newSectionRefs
  );

  // Set up inverse action (undo/redo)
  const undoMetadata = getAddPanelsBySpecUndoMetadata(prevState, newState, ref);
  const inverseAction = Actions.addPanelsBySpecUndo(ref, undoMetadata);

  return [newState, inverseAction];
};

/**
 * Compares view reducer state before and after
 * processing addPanelsBySpec or addPanelsBySpecUndo.
 * Returns list of section metadata describing how to
 * restore panel bank sections and panels to original state.
 */
function getAddPanelsBySpecUndoMetadata(
  prevState: ViewReducerState,
  newState: ViewReducerState,
  ref: Types.Ref
): Types.UndoAddPanelsBySpecSectionMetadata[] {
  const prevPanelBank = prevState.parts[ref.type][ref.id];
  return prevPanelBank.sectionRefs.map(sectionRef => {
    const prevSection = prevState.parts[sectionRef.type][sectionRef.id];
    const newSection = newState.parts[sectionRef.type][sectionRef.id];
    const isPanelRefsChanged = prevSection.panelRefs !== newSection.panelRefs;
    const isOpenChanged = prevSection.isOpen !== newSection.isOpen;
    return {
      sectionRef,
      isPanelsAuto: prevSection.isPanelsAuto,
      panelRefs: isPanelRefsChanged ? prevSection.panelRefs : undefined,
      isOpen: isOpenChanged ? prevSection.isOpen : undefined,
    };
  });
}

export const updateAllLinePlotSectionSettings = (
  state: ViewReducerState,
  ref: Types.Ref,
  settings: Partial<LinePlotSettings> | undefined
): [ViewReducerState, ActionType] => {
  const newState = setInShallowClone(
    state,
    ['parts', 'panel-bank-section-config'],
    Object.assign({}, state.parts['panel-bank-section-config'])
  );

  const prevSectionConfigs: PanelBankSectionConfigNormalized[] = [];

  state.parts[ref.type][ref.id].sectionRefs.forEach((sectionRef, idx) => {
    const section = state.parts[sectionRef.type][sectionRef.id];
    prevSectionConfigs.push(section);

    newState.parts[sectionRef.type][sectionRef.id] = {
      ...newState.parts[sectionRef.type][sectionRef.id],
      sectionSettings: {
        ...newState.parts[sectionRef.type][sectionRef.id].sectionSettings,
        linePlot: {
          ...newState.parts[sectionRef.type][sectionRef.id].sectionSettings
            ?.linePlot,
          ...settings,
        },
      },
    };
  });

  const inverseAction = ActionsInternal.setAllSections(ref, prevSectionConfigs);

  return [newState, inverseAction];
};

export const updateSettings = (
  state: ViewReducerState,
  ref: Types.Ref,
  settings: Partial<PanelBankSettings>
): [ViewReducerState, ActionType] => {
  const prev = state.parts[ref.type][ref.id].settings;

  const newState = setInShallowClone(
    state,
    ['parts', ref.type, ref.id, 'settings'],
    {
      ...prev,
      ...settings,
    }
  );

  const inverseAction = Actions.updateSettings(ref, prev);

  return [newState, inverseAction];
};
